import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  CreateServiceAPI,
  UpdateServiceAPI,
  GetAllDepartment,
} from "../../../lib/api";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { RouteNavigation } from "../../../Routes";
import Cookies from "js-cookie";
import { useState } from "react";
import React, { useEffect } from "react";

const CreateService = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const serviceData = location?.state?.serviceData;
  const SerID = serviceData?.SerID;
  const [isLoading, setIsLoading] = useState(false);

  const { data: departmentsData } = useQuery({
    queryKey: ["GetAllDepartment"],
    queryFn: () => GetAllDepartment(),
  });

  const {
    register,
    handleSubmit,
    setFocus,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      serName: serviceData?.SerName || "",
      serDepID: serviceData?.SerDepID || "",
      serStatus: serviceData?.SerStatus ?? true,
    },
  });

  // Set initial department value and watch for changes
  const selectedDepartmentId = watch("serDepID");
  const [showInactiveDepartmentWarning, setShowInactiveDepartmentWarning] =
    useState(false);

  useEffect(() => {
    if (serviceData) {
      // Set initial values when in update mode
      setValue("serName", serviceData.SerName);
      setValue("serDepID", serviceData.SerDepID);
      setValue("serStatus", serviceData.SerStatus);

      // Check if the current department is inactive
      const currentDepartment = departmentsData?.data?.department?.find(
        (dept) => dept.DepId === serviceData.SerDepID
      );
      if (currentDepartment && !currentDepartment.Status) {
        setShowInactiveDepartmentWarning(true);
      }
    }
    setFocus("serName");
  }, [serviceData, setFocus, setValue, departmentsData]);

  const mutate = useMutation({
    mutationFn: (data) => UpdateServiceAPI(data, SerID),
    onMutate: () => {
      setIsLoading(true);
    },
    onSuccess: () => {
      toast.success("Service updated successfully", {
        position: "top-right",
        closeOnClick: true,
      });
      navigate(RouteNavigation.service);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to update service", {
        position: "top-right",
        closeOnClick: true,
      });
    },
    onSettled: () => {
      setIsLoading(false);
    },
  });

  const handleServiceSubmit = async (data) => {
    setIsLoading(true);
    try {
      const dataResponse = await CreateServiceAPI(data);
      console.log(dataResponse,"dataResponse");
      
      if (dataResponse.status === 200) {
        toast.success("Service created successfully", {
          position: "top-right",
          closeOnClick: true,
        });
        navigate(RouteNavigation.service);
      } else {
        toast.error(dataResponse.error || "Failed to create service", {
          position: "top-right",
          closeOnClick: true,
        });
      }
    } catch (error) {
      if (error.response && error.response.status === 500) {
        toast.error("Service already exists. Please use a different name.", {
          position: "top-right",
          closeOnClick: true,
        });
      } else {
        toast.error("An unexpected error occurred. Please try again.", {
          position: "top-right",
          closeOnClick: true,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data) => {
    const CompanyId = Cookies.get("CompanyId");
    const payload = {
      ...data,
      serCompanyID: CompanyId,
      serDepID: data.serDepID,
      serName: data.serName,
      serStatus: data.serStatus,
    };

    if (serviceData) {
      mutate.mutate(payload);
    } else {
      handleServiceSubmit(payload);
    }
  };

  // Handle department selection change
  const handleDepartmentChange = (e) => {
    const selectedDeptId = e.target.value;
    setValue("serDepID", selectedDeptId);
    // Check if selected department is inactive
    const selectedDept = departmentsData?.data?.department?.find(
      (dept) => dept.DepId === selectedDeptId
    );
    setShowInactiveDepartmentWarning(selectedDept && !selectedDept.Status);
  };

  return (
    <div className="py-5">
      <h4 className="text-3xl font-bold mb-5">
        {serviceData ? "Update" : "Create"} Service
      </h4>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col w-full mt-5"
      >
        <div className="flex gap-10">
          <div className="w-full">
            <label className="font-semibold">Service Name</label>
            <input
              className="flex items-center h-12 px-4 w-full bg-gray-200 mt-2 rounded focus:outline-none focus:ring-2"
              {...register("serName", {
                required: "Service name is required",
              })}
            />
            {errors.serName && (
              <span className="text-red-600">{errors.serName.message}</span>
            )}
          </div>
          <div className="w-full">
            <label className="font-semibold">Department</label>
            <select
              {...register("serDepID", {
                required: "Department is required",
                onChange: handleDepartmentChange,
              })}
              className="border-[1px] p-2 w-full h-12 mt-2 rounded-lg focus:outline-none focus:ring-2 bg-gray-200"
            >
              <option value="" disabled>
                Select Department
              </option>
              {departmentsData?.data?.department?.map((dept) => (
                <option
                  key={dept.DepID}
                  value={dept.DepID}
                  disabled={
                    !dept.Status && dept.DepID !== serviceData?.SerDepID
                  }
                >
                  {dept.DepName} {!dept.Status && "(Inactive)"}
                </option>
              ))}
            </select>
            {errors.serDepID && (
              <p className="text-red-600">{errors.serDepID.message}</p>
            )}
            {showInactiveDepartmentWarning && (
              <p className="text-yellow-600">
                Warning: The selected department is inactive. This may affect
                service availability.
              </p>
            )}
          </div>
        </div>
        <div className="flex gap-4 mt-6 items-center">
          <input
            {...register("serStatus")}
            type="checkbox"
            id="serviceStatus"
            className="w-4 h-4"
          />
          <label htmlFor="serviceStatus" className="font-medium">
            Active
          </label>
        </div>
        <div className="flex gap-5 justify-end mt-8">
          <Link to={RouteNavigation.service}>
            <button
              type="button"
              className="flex items-center justify-center h-10 px-6 bg-gray-600 rounded font-semibold text-sm text-white hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
          </Link>
          <button
            type="submit"
            disabled={isLoading}
            className="flex items-center justify-center h-10 px-6 bg-blue-600 rounded font-semibold text-sm text-white hover:bg-blue-700 disabled:bg-blue-400 transition-colors"
          >
            {isLoading
              ? "Processing..."
              : serviceData
              ? "Update Service"
              : "Create Service"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateService;
